/**
 * My Indigrey — account client (auth-agnostic).
 *
 * Renders the dashboard from the Indigrey Account Service for customers who
 * signed in with phone OTP (no Shopify storefront session). The service owns
 * WHICH provider verified the phone; this file only knows:
 *   1. open the provider UI (today: KwikPass popup)   2. POST the credential
 *   3. render My Indigrey from /v1/* JSON
 * Swap the provider server-side and this file keeps working untouched.
 * If the Liquid `customer` object rendered the page (email path), this file
 * does nothing — the server-rendered dashboard is already present.
 */
(function () {
  "use strict";
  var root = document.getElementById("ig-acct");
  if (!root || root.dataset.igMode !== "app") return;      // Liquid path active → do nothing
  var SERVICE = (root.dataset.igService || "").replace(/\/$/, "");
  if (!SERVICE) return;
  var app = document.getElementById("ig-app");
  var loginPanel = document.getElementById("ig-login-panel");
  var TOKEN_KEY = "ig_session_token";
  var PROVIDER = "kwikpass";   // updated from /v1/config; decides which sign-in UI to show
  // KwikPass mode + identity-token source come from the section settings (data-*),
  // so switching which token we read is a THEME CONFIG change, never a code change.
  //   plugin   -> KwikPass popup (app-embed) owns login; we read the rotated token
  //   headless -> KwikPass headless SDK (kpSendOTP/kpVerifyOTP) drives our own form
  var KP_MODE = (root.dataset.igKpMode || "plugin").toLowerCase();
  var TOKEN_SOURCE = root.dataset.igKpToken || "KWIKUSERTOKEN";
  // DEV ONLY: append ?ig_diag=1 to the URL to print client-side auth diagnostics.
  // Off by default; logs no secrets and no full token — just structural facts.
  var DIAG = /[?&]ig_diag=1(?:&|$)/.test(location.search || "");
  var otpPhone = null;         // remembered between "send code" and "verify" (msg91)

  // ---------- tiny helpers ----------
  function token(v) {
    if (v === undefined) { try { return sessionStorage.getItem(TOKEN_KEY); } catch (e) { return null; } }
    try { v === null ? sessionStorage.removeItem(TOKEN_KEY) : sessionStorage.setItem(TOKEN_KEY, v); } catch (e) {}
  }
  function api(path, opts) {
    opts = opts || {};
    var headers = { "Content-Type": "application/json" };
    var t = token();
    if (t) headers.Authorization = "Bearer " + t;
    return fetch(SERVICE + path, {
      method: opts.method || "GET",
      headers: headers,
      credentials: "include",
      body: opts.body ? JSON.stringify(opts.body) : undefined,
    }).then(function (r) {
      if (r.status === 401) { token(null); throw { code: 401 }; }
      return r.json().then(function (j) { if (!r.ok) throw j; return j; });
    });
  }
  function esc(s) { return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]; }); }
  function money(m) { return m ? "₹" + Number(m.amount || m).toLocaleString("en-IN") : ""; }
  function dt(iso) { return iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""; }
  function chip(text, good, bad) { return '<span class="ig-chip' + (good ? " ig-chip--good" : "") + (bad ? " ig-chip--bad" : "") + '">' + esc(text) + "</span>"; }
  function pretty(s) { s = String(s || "").replace(/_/g, " ").toLowerCase(); return s.charAt(0).toUpperCase() + s.slice(1); }
  // Prefer the transparent, product-only flat-lay PNG (by title) for any line item;
  // fall back to whatever image the order carries. Keeps every picture gutter-free.
  var _flTitle = null;
  function itemImage(li) {
    if (_flTitle === null) { try { var el = document.querySelector("[data-ig-flatlays-title]"); _flTitle = el ? (JSON.parse(el.textContent || "{}") || {}) : {}; } catch (e) { _flTitle = {}; } }
    var k = String(li && li.title || "").toLowerCase().trim();
    return _flTitle[k] || (li && li.image) || "";
  }
  // Reveal-on-arrival (settle) motion for app-mode cards. Arms elements to opacity:0
  // then lets them rise in as they enter view. Safe: if IntersectionObserver is
  // missing OR hasn't fired within 1.2s, everything is force-revealed — content is
  // never left invisible.
  var _riseIO = null;
  function armRise(root) {
    var els = (root || document).querySelectorAll(".ig-acct [data-ig-rise]:not(.ig-rise-armed)");
    if (!("IntersectionObserver" in window) || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return; // leave visible
    if (!_riseIO) _riseIO = new IntersectionObserver(function (es) { es.forEach(function (e) { if (e.isIntersecting) { e.target.classList.add("ig-in"); _riseIO.unobserve(e.target); } }); }, { threshold: .08, rootMargin: "0px 0px -4% 0px" });
    els.forEach(function (el) { el.classList.add("ig-rise-armed"); _riseIO.observe(el); });
    setTimeout(function () { els.forEach(function (el) { el.classList.add("ig-in"); }); }, 1200); // fail-safe reveal
  }
  // DEV diagnostics helpers (no-ops unless ?ig_diag=1). Never log full token/secret.
  function diag() { if (DIAG && window.console) { try { console.log.apply(console, ["[IG-DIAG]"].concat([].slice.call(arguments))); } catch (e) {} } }
  function jweHeader(t) { try { var s = String(t).split(".")[0].replace(/-/g, "+").replace(/_/g, "/"); return JSON.parse(atob(s + "===".slice((s.length + 3) % 4))); } catch (e) { return {}; } }

  // ---------- sign-in flow (provider-aware) ----------
  // Both active providers (KwikPass headless SDK, MSG91 self-hosted) use the SAME
  // phone -> OTP form; only the send/verify calls differ. This is the whole point
  // of the provider boundary: My Indigrey never changes when the provider does.
  function startSignin() {
    ensureProvider().then(function () {
      if (PROVIDER === "kwikpass" && KP_MODE !== "headless") {
        // PLUGIN: let the KwikPass popup own the login UI, then read the token.
        renderKpWaiting();
        var opened = triggerKpPopup();
        if (!opened) { renderKpWaiting("Tap “Open sign-in” to continue."); }
        watchProviderLogin();
      } else if (PROVIDER === "kwikpass" || PROVIDER === "msg91") {
        renderOtpForm();   // headless SDK (kwikpass) or self-hosted (msg91): our own form
      } else {
        var b = document.getElementById("ig-signin-btn");
        if (b) window.location.href = b.getAttribute("href");   // last-resort: hosted email code
      }
    });
  }
  document.addEventListener("click", function (e) {
    if (e.target.closest("#ig-signin-btn")) { e.preventDefault(); startSignin(); }
  });

  // ---- KwikPass headless SDK bridge (official "Custom Headless Stores" flow) ----
  function kpSdk() { return window.__KP_LOGIN_SDK_INSTANCE__; }
  function kpSendOtp(phone10) {
    var s = kpSdk();
    if (!s || typeof s.kpSendOTP !== "function") return Promise.reject({ error: "Login is still loading — please try again in a moment." });
    return Promise.resolve(s.kpSendOTP(phone10)).then(function (r) {
      if (r && r.status === 200) return r;
      throw { error: (r && r.message) || "Could not send code. Try again." };
    });
  }
  function kpVerifyOtp(phone10, code) {
    var s = kpSdk();
    if (!s || typeof s.kpVerifyOTP !== "function") return Promise.reject({ error: "Login is still loading — please try again." });
    return Promise.resolve(s.kpVerifyOTP({ phone: phone10, otp: Number(code) })).then(function (r) {
      var kpToken = r && r.body && r.body.data && r.body.data.kpToken;
      if (r && r.status === 200 && kpToken) return kpToken;    // JWE -> our Worker decrypts it
      throw { error: (r && r.message) || "Incorrect code. Please try again." };
    });
  }

  // ---- Shared phone -> OTP sign-in UI ----
  function renderOtpForm() {
    ensureOtpStyles();
    var codeLen = PROVIDER === "kwikpass" ? 4 : 6;   // KwikPass OTP is 4-digit
    loginPanel.hidden = false;
    loginPanel.innerHTML =
      '<div class="ig-acct__login"><p class="ig-acct__eyebrow">My Indigrey</p>' +
      '<h1 class="ig-acct__title">Sign in</h1>' +
      '<p class="ig-acct__meta">Enter your mobile number — we&#39;ll send a one-time code.</p>' +
      '<div class="ig-otp" id="ig-otp">' +
        '<div data-step="phone">' +
          '<div class="ig-otp-phone"><span class="ig-otp-cc">+91</span>' +
          '<input class="ig-otp-input" id="ig-otp-phone" type="tel" inputmode="numeric" maxlength="10" placeholder="10-digit mobile number" autocomplete="tel" aria-label="Mobile number"></div>' +
          '<button class="ig-btn ig-btn--primary ig-otp-btn" id="ig-otp-send">Send code</button>' +
        '</div>' +
        '<div data-step="code" hidden>' +
          '<input class="ig-otp-input" id="ig-otp-code" type="text" inputmode="numeric" maxlength="' + codeLen + '" placeholder="' + codeLen + '-digit code" autocomplete="one-time-code" aria-label="One-time code">' +
          '<button class="ig-btn ig-btn--primary ig-otp-btn" id="ig-otp-verify">Verify &amp; sign in</button>' +
          '<button class="ig-btn ig-otp-btn" id="ig-otp-back">Change number</button>' +
        '</div>' +
        '<p class="ig-otp-msg" id="ig-otp-msg" role="status" aria-live="polite"></p>' +
        '<p class="ig-acct__note">No passwords, ever.</p>' +
        '<div class="ig-acct__altlogin"><span class="ig-acct__altrule"></span>' +
        '<a class="ig-acct__altlink ig-tap" href="/account/login?return_url=%2Fpages%2Fmy-indigrey">Prefer email? Sign in with email</a></div>' +
      '</div></div>';
    var msg = document.getElementById("ig-otp-msg");
    function show(step) {
      loginPanel.querySelector('[data-step="phone"]').hidden = step !== "phone";
      loginPanel.querySelector('[data-step="code"]').hidden = step !== "code";
      msg.textContent = "";
    }
    function digits(v) { return String(v || "").replace(/\D/g, "").slice(-10); }

    // Provider-specific SEND
    function doSend(phone10) {
      if (PROVIDER === "kwikpass") return kpSendOtp(phone10);
      return api("/v1/auth/request", { method: "POST", body: { phone: "+91" + phone10 } });
    }
    // Provider-specific VERIFY -> resolves once our session is minted
    function doVerify(phone10, code) {
      if (PROVIDER === "kwikpass") {
        return kpVerifyOtp(phone10, code).then(function (kpToken) {
          return api("/v1/auth/verify", { method: "POST", body: { credential: kpToken } });
        });
      }
      return api("/v1/auth/verify", { method: "POST", body: { phone: "+91" + phone10, credential: code } });
    }

    document.getElementById("ig-otp-send").addEventListener("click", function () {
      var ph = digits(document.getElementById("ig-otp-phone").value);
      if (ph.length !== 10) { msg.textContent = "Enter a valid 10-digit mobile number."; return; }
      otpPhone = ph;
      this.disabled = true; msg.textContent = "Sending code…";
      doSend(ph)
        .then(function () { show("code"); var ci = document.getElementById("ig-otp-code"); ci && ci.focus(); })
        .catch(function (err) { msg.textContent = (err && err.error) || "Could not send code. Try again."; })
        .then(function () { var b = document.getElementById("ig-otp-send"); if (b) b.disabled = false; });
    });
    document.getElementById("ig-otp-verify").addEventListener("click", function () {
      var code = String(document.getElementById("ig-otp-code").value || "").replace(/\D/g, "");
      if (!code) { msg.textContent = "Enter the code we sent you."; return; }
      this.disabled = true; msg.textContent = "Verifying…";
      doVerify(otpPhone, code)
        .then(function (res) { if (res && res.token) token(res.token); loginPanel.hidden = true; boot(); })
        .catch(function (err) { msg.textContent = (err && err.error) || "Incorrect code."; var b = document.getElementById("ig-otp-verify"); if (b) b.disabled = false; });
    });
    document.getElementById("ig-otp-back").addEventListener("click", function () { show("phone"); });
    var pi = document.getElementById("ig-otp-phone"); if (pi) pi.focus();
  }

  function ensureOtpStyles() {
    if (document.getElementById("ig-otp-styles")) return;
    var s = document.createElement("style"); s.id = "ig-otp-styles";
    s.textContent = ".ig-otp{max-width:360px;margin:26px auto 0}.ig-otp-phone{display:flex;align-items:stretch;border:1px solid var(--ig-line,#E8E4DD);background:#fff;margin-bottom:12px}.ig-otp-cc{display:flex;align-items:center;padding:0 14px;color:#1a1714;font-family:'Jost',sans-serif;font-size:15px;letter-spacing:.04em;border-right:1px solid var(--ig-line,#E8E4DD)}.ig-otp-input{width:100%;border:1px solid var(--ig-line,#E8E4DD);background:#fff;padding:14px;font-family:'Jost',sans-serif;font-size:15px;letter-spacing:.04em;color:#1a1714}.ig-otp-phone .ig-otp-input{border:0;margin:0}.ig-otp-input::placeholder{color:#a8a296}.ig-otp-input:focus{outline:none;border-color:#1a1714}#ig-otp-code{text-align:center;letter-spacing:.5em;font-size:19px;margin-bottom:12px}.ig-otp-btn{display:block;width:100%;margin:0 0 10px}.ig-otp-msg{font-size:13px;color:#8a2f2f;min-height:18px;margin:4px 0 0;text-align:center}.ig-acct__altlogin{margin:22px 0 0;text-align:center}.ig-acct__altrule{display:block;height:1px;background:var(--ig-line,#E8E4DD);margin:0 0 18px}.ig-acct__altlink{display:inline-block;font-family:'Jost',sans-serif;font-size:12px;letter-spacing:.1em;text-transform:uppercase;color:#7c766c;text-decoration:none;border-bottom:1px solid #d8d2c8;padding:5px 3px 3px;transition:color .2s,border-color .2s,opacity .18s;-webkit-tap-highlight-color:transparent}@media(hover:hover){.ig-acct__altlink:hover{color:#1a1714;border-color:#1a1714}}.ig-acct__altlink:active{color:#1a1714;border-color:#1a1714;opacity:.55}.ig-email-input{margin-bottom:12px}.ig-acct__forgot{display:inline-block;margin:8px 0 0;font-family:'Jost',sans-serif;font-size:11.5px;letter-spacing:.05em;color:#7c766c;text-decoration:none;transition:color .2s,opacity .18s;-webkit-tap-highlight-color:transparent}@media(hover:hover){.ig-acct__forgot:hover{color:#1a1714}}.ig-acct__forgot:active{color:#1a1714;opacity:.55}";
    document.head.appendChild(s);
  }

  // Read the KwikPass identity token named by TOKEN_SOURCE. KwikPass exposes its
  // tokens in three places depending on the token — a window global (e.g.
  // kp_customer_id), a cookie, and/or localStorage — so we check all three by the
  // SAME configured name. Change TOKEN_SOURCE (theme setting) to point at whichever
  // token GoKwik confirms carries the phone; no code change needed.
  function readProviderToken(name) {
    name = name || TOKEN_SOURCE;
    try { if (typeof window[name] === "string" && window[name]) return window[name]; } catch (e) {}
    var esc = name.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
    var m = document.cookie.match(new RegExp("(?:^|;\\s*)" + esc + "=([^;]+)"));
    if (m) { try { return decodeURIComponent(m[1]); } catch (e) { return m[1]; } }
    try { var v = localStorage.getItem(name); if (v) return v; } catch (e) {}
    return null;
  }
  // Best-effort "the KwikPass session is authenticated" signal (set on real login).
  function kpLoggedIn() {
    try { if (localStorage.getItem("gk_login_source") === "kwik_pass") return true; } catch (e) {}
    return /(?:^|;\s*)gk_login_source=kwik_pass/.test(document.cookie);
  }
  // Open the KwikPass popup (app-embed). Returns true if we could trigger it.
  function triggerKpPopup() {
    try { if (typeof window.kp_trigger_popup === "function") { window.kp_trigger_popup(); return true; } } catch (e) {}
    var el = document.querySelector('[data-kp-login], .kwikpass-login, #kp-login-button, [id*="kwikpass"] button, [class*="kwikpass"] button');
    if (el) { el.click(); return true; }
    return false;
  }
  // Waiting panel shown while the customer completes the KwikPass popup.
  function renderKpWaiting(note) {
    app.innerHTML = "";
    if (!loginPanel) return;
    loginPanel.hidden = false;
    loginPanel.innerHTML =
      '<p class="ig-acct__eyebrow">My Indigrey</p>' +
      '<h1 class="ig-acct__title">Signing you in…</h1>' +
      '<p class="ig-acct__meta">' + esc(note || "Complete the sign-in in the pop-up — this page continues automatically.") + '</p>' +
      '<div class="ig-acct__login-actions"><button id="ig-signin-btn" class="ig-btn ig-btn--primary" type="button">Open sign-in</button></div>' +
      '<p class="ig-acct__note">No passwords, ever.</p>';
  }
  // Default sign-in prompt (rebuilds the panel if a waiting/other state replaced it).
  function showLoginPanel() {
    app.innerHTML = "";
    if (!loginPanel) return;
    loginPanel.hidden = false;
    loginPanel.innerHTML =
      '<p class="ig-acct__eyebrow">My Indigrey</p>' +
      '<h1 class="ig-acct__title">Sign in to your account</h1>' +
      '<p class="ig-acct__meta">Track orders, manage addresses and enjoy a faster checkout.</p>' +
      '<div class="ig-acct__login-actions"><button id="ig-signin-btn" class="ig-btn ig-btn--primary" type="button">Sign in</button></div>' +
      '<p class="ig-acct__note">We&#39;ll send you a one-time code — no passwords, ever.</p>';
  }
  // Detect a successful popup login. Primary mechanism is EVENT-DRIVEN: the
  // KwikPass login runs in an iframe (IS_KP_IFRAME_LOADED), so a postMessage,
  // the popup closing (focus/visibilitychange), or a storage write all trigger an
  // immediate re-read of OUR token. We never parse GoKwik's payload — the event is
  // only a "look now" nudge. A 1s poll remains as a BOUNDED fallback (never runs
  // forever), and everything is torn down on success or timeout.
  // If GoKwik confirms an official login-success callback, wire it into recheck().
  var watching = false;
  function watchProviderLogin() {
    if (watching) return;
    watching = true;
    var before = readProviderToken();
    var tries = 0, timer = null;
    function cleanup() {
      if (timer) { clearInterval(timer); timer = null; }
      window.removeEventListener("message", recheck);
      window.removeEventListener("focus", recheck);
      document.removeEventListener("visibilitychange", recheck);
      window.removeEventListener("storage", recheck);
      watching = false;
    }
    function recheck() {
      var cred = readProviderToken();
      if (cred && cred !== before) { diag("login detected — token rotated (" + TOKEN_SOURCE + ")"); cleanup(); exchange(cred); }
    }
    window.addEventListener("message", recheck);
    window.addEventListener("focus", recheck);
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("storage", recheck);
    timer = setInterval(function () {
      tries++;
      var cred = readProviderToken();
      if (cred && cred !== before) { cleanup(); exchange(cred); }
      else if (tries > 120) { cleanup(); showLoginPanel(); }   // ~2 min hard stop
    }, 1000);
  }
  // POST the KwikPass token to our Worker, which decrypts it and mints our session.
  // silent=true is the returning-visitor auto-login: on failure we quietly show the
  // sign-in panel instead of an error.
  function exchange(cred, silent) {
    if (!silent) setBusy("Signing you in…");
    if (DIAG) { var h = jweHeader(cred); diag("verify → Worker", { tokenSource: TOKEN_SOURCE, credentialLen: cred ? cred.length : 0, jweAlg: h.alg || null, jweEnc: h.enc || null }); }
    api("/v1/auth/verify", { method: "POST", body: { credential: cred, tokenSource: TOKEN_SOURCE } })
      .then(function (res) { diag("verify ✓ signed in"); if (res.token) token(res.token); boot(); })
      .catch(function (err) {
        diag("verify ✗", err);
        if (silent) { showLoginPanel(); }
        else { setError(err && err.error ? err.error : "Sign-in failed. Please try again."); }
      });
  }

  // ---------- boot ----------
  function boot() {
    // One round-trip: profile + first page of orders + addresses together.
    api("/v1/bootstrap").then(function (b) {
      loginPanel && (loginPanel.hidden = true);
      state.orders = b.orders || [];
      state.pageInfo = b.ordersPageInfo || { hasNextPage: false };
      state.addresses = b.addresses || [];
      state.defaultId = b.defaultId || null;
      if (isReturnsDemo()) seedDemoOrder();   // preview: make the first order delivered + in-window across all tabs
      renderShell(b);            // b has { profile, profileComplete, reward }
      renderWardrobe(); renderOrders();
      renderAddresses();
    }).catch(function (err) {
      if (err.code === 401) {
        // No Indigrey session yet — NOW learn the provider (only needed to pick the sign-in UI).
        app.innerHTML = "";
        ensureProvider().then(function () {
          if (PROVIDER === "kwikpass" && KP_MODE !== "headless") {
            // PLUGIN: if the visitor is already KwikPass-authenticated, sign them
            // into My Indigrey silently; otherwise show the sign-in prompt.
            if (kpLoggedIn()) {
              var cred = readProviderToken();
              if (cred) { exchange(cred, true); return; }
            }
            showLoginPanel();
          } else if (PROVIDER === "kwikpass" || PROVIDER === "msg91") {
            renderOtpForm();
          } else {
            loginPanel && (loginPanel.hidden = false);
          }
        });
      } else setError("Couldn't reach your account right now.");
    });
  }
  var _providerLoaded = false;
  function ensureProvider() {
    if (_providerLoaded) return Promise.resolve();
    return api("/v1/config").then(function (cfg) { PROVIDER = cfg.provider || "kwikpass"; _providerLoaded = true; })
      .catch(function () { _providerLoaded = true; });
  }
  function setBusy(msg) { app.innerHTML = '<p class="ig-acct__empty">' + esc(msg) + "</p>"; }
  function setError(msg) { app.innerHTML = '<p class="ig-acct__empty">' + esc(msg) + "</p>"; }

  // ---------- shell ----------
  var state = { me: null, orders: [], pageInfo: null, addresses: [], defaultId: null };
  // Personal, time-of-day greeting (matches the logged-in server path). A few
  // phrasings per bucket, picked at random so each sign-in feels a little alive.
  function igGreet(name) {
    var h = new Date().getHours(), set;
    if (h < 5)        set = ["Hello night owl", "Burning the midnight oil", "Still awake"];
    else if (h < 12)  set = ["Good morning", "Bright and early", "A new morning"];
    else if (h < 17)  set = ["Good afternoon", "Good to see you", "Hello again"];
    else if (h < 21)  set = ["Good evening", "Winding down", "Evening"];
    else              set = ["Good evening", "A calm night", "Night owl hours"];
    var g = set[Math.floor(Math.random() * set.length)];
    return name ? g + ", " + esc(name) + "." : g + ".";
  }
  function renderShell(me) {
    state.me = me;
    var p = me.profile;
    app.innerHTML =
      '<header class="ig-acct__head"><p class="ig-acct__eyebrow">My Indigrey</p>' +
      '<h1 class="ig-acct__title">' + igGreet(p.firstName) + "</h1>" +
      '<a href="#" class="ig-acct__logout" data-ig-app-logout>Log out</a></header>' +
      '<nav class="ig-acct__tabs" aria-label="Account">' +
      tab("wardrobe", "Wardrobe", true) + tab("orders", "Orders") + tab("returns", "Returns") + tab("profile", "Profile") +
      '</nav>' +
      '<section class="ig-acct__panel is-active" data-ig-panel="wardrobe"><div id="ig-wardrobe"><p class="ig-acct__empty">Loading…</p></div></section>' +
      '<section class="ig-acct__panel" data-ig-panel="orders"><div id="ig-orders"><p class="ig-acct__empty">Loading…</p></div></section>' +
      '<section class="ig-acct__panel" data-ig-panel="returns"><div id="ig-returns"><p class="ig-acct__empty">Loading…</p></div></section>' +
      '<section class="ig-acct__panel" data-ig-panel="profile"><div id="ig-profile"></div><div id="ig-addresses" class="ig-acct__addrblock"><p class="ig-acct__empty">Loading…</p></div></section>';
    renderProfile();
    if (!renderShell._bound) { bindShell(); renderShell._bound = true; }
    var hash = (location.hash || "").replace("#", "");
    if (hash === "addresses") hash = "profile";   // addresses now live inside Profile
    if (hash) {
      var tb = app.querySelector('[data-ig-tab="' + hash + '"]');
      if (tb) tb.click();
    }
  }
  function tab(name, label, active) { return '<a href="#' + name + '" class="ig-acct__tab ig-tap' + (active ? " is-active" : "") + '" data-ig-tab="' + name + '">' + label + "</a>"; }
  // Full sign-out. Clearing only OUR token isn't enough: boot() sees the KwikPass
  // session still live (gk_login_source) and silently re-authenticates. So we also
  // clear KwikPass's own client state (token + every gk_/kp_/kwik cookie & localStorage
  // key) and, if present, call its SDK logout — then reload to a clean login prompt.
  function logout() {
    // Local cleanup first: our Bearer token + any stale provider keys. This alone
    // does NOT sign the user out — the live session is an httpOnly cookie set on
    // the account-service (worker) domain, which this page (indigrey.in) can
    // neither read nor delete. Only the service's /v1/auth/logout can drop it.
    token(null);
    var KILL = ["gk_login_source", TOKEN_SOURCE, "KWIKUSERTOKEN", "gk_customer_id", "kp_customer_id", "kp_access_token"];
    var isKp = function (k) { return k && (/^(gk_|kp_|kwik)/i.test(k) || KILL.indexOf(k) !== -1); };
    try { for (var i = localStorage.length - 1; i >= 0; i--) { var lk = localStorage.key(i); if (isKp(lk)) localStorage.removeItem(lk); } } catch (e) {}
    try { for (var j = sessionStorage.length - 1; j >= 0; j--) { var sk = sessionStorage.key(j); if (isKp(sk) || sk === TOKEN_KEY) sessionStorage.removeItem(sk); } } catch (e) {}
    // THE FIX: wait for the service to clear its session cookie BEFORE navigating.
    // A synchronous location.replace() here cancels the in-flight logout request,
    // so the cookie survives and the reload silently re-authenticates — which is
    // exactly why sign-out kept failing. Reload only after the call settles, with
    // a fail-safe so a stalled request can never trap the user on the account.
    var reloaded = false;
    var go = function () { if (reloaded) return; reloaded = true; location.replace(location.pathname); };
    api("/v1/auth/logout", { method: "POST" }).then(go, go);
    setTimeout(go, 3500);
  }
  function bindShell() {
    app.addEventListener("click", function (e) {
      var t = e.target.closest("[data-ig-tab]");
      if (t) {
        e.preventDefault();
        var name = t.getAttribute("data-ig-tab");
        app.querySelectorAll("[data-ig-panel]").forEach(function (pn) { pn.classList.toggle("is-active", pn.getAttribute("data-ig-panel") === name); });
        app.querySelectorAll("[data-ig-tab]").forEach(function (tb) { tb.classList.toggle("is-active", tb === t); });
        if (name === "returns") loadReturns();
        history.replaceState(null, "", "#" + name);
      }
      var lo = e.target.closest("[data-ig-app-logout]");
      if (lo) { e.preventDefault(); logout(); }
    });
  }

  // ---------- orders ----------
  function loadOrders(after) {
    api("/v1/orders" + (after ? "?after=" + encodeURIComponent(after) : "")).then(function (res) {
      state.orders = after ? state.orders.concat(res.orders) : res.orders;
      state.pageInfo = res.pageInfo;
      renderWardrobe();
      renderOrders();
    }).catch(function () { document.getElementById("ig-orders").innerHTML = '<p class="ig-acct__empty">Couldn’t load orders.</p>'; });
  }
  function trackBtns(o, primary) {
    return (o.tracking || []).filter(function (t) { return /^https?:/i.test(t.url || ""); }).slice(0, 1).map(function (t) {
      return '<a class="ig-btn ig-btn--sm' + (primary ? " ig-btn--primary" : "") + '" href="' + esc(t.url) + '" target="_blank" rel="noopener">Track</a>';
    }).join("");
  }
  // ---------- wardrobe ----------
  // The signature My Indigrey view: each garment you own, once, newest first,
  // with the craft portrait (pieces · hours of handwork · stitches) summed from
  // the products' craft metafields. Same markup + CSS as the email-login path,
  // so it looks identical on phone-OTP accounts. Empty = the designed invitation.
  function monthYear(iso) { try { return new Date(iso).toLocaleDateString("en-IN", { month: "short", year: "numeric" }); } catch (e) { return ""; } }
  // In-account piece detail — the panel markup + its open/populate/close wiring.
  function igwDetailHTML() {
    return '<div class="igw-detail" data-ig-detail hidden>' +
      '<button class="igw-detail__scrim" data-ig-detail-close aria-label="Close piece detail"></button>' +
      '<div class="igw-detail__panel" role="dialog" aria-modal="true" aria-label="Piece detail">' +
        '<button class="igw-detail__x" data-ig-detail-close aria-label="Close"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg></button>' +
        '<div class="igw-detail__media"><img class="igw-detail__img" data-ig-detail-img alt=""><div class="igw-detail__thumbs" data-ig-detail-thumbs></div></div>' +
        '<div class="igw-detail__info">' +
          '<p class="igw-detail__eyebrow" data-ig-detail-owned></p>' +
          '<h2 class="igw-detail__name" data-ig-detail-name></h2>' +
          '<p class="igw-detail__color" data-ig-detail-color></p>' +
          '<div class="igw-detail__craft" data-ig-detail-craft></div>' +
          '<p class="igw-detail__honest">Craft figures are careful estimates from our production process, a reflection of the hands behind each piece.</p>' +
          '<a class="igw-detail__link" data-ig-detail-link href="#">View this piece in store</a>' +
        "</div></div></div>";
  }
  function igwDetailBind(host) {
    var detail = host.querySelector("[data-ig-detail]"); if (!detail) return;
    var imgEl = detail.querySelector("[data-ig-detail-img]"), thumbs = detail.querySelector("[data-ig-detail-thumbs]");
    var ownedEl = detail.querySelector("[data-ig-detail-owned]"), nameEl = detail.querySelector("[data-ig-detail-name]");
    var colorEl = detail.querySelector("[data-ig-detail-color]"), craftEl = detail.querySelector("[data-ig-detail-craft]"), linkEl = detail.querySelector("[data-ig-detail-link]");
    var lastY = 0;
    function row(k, v) { return v ? '<div class="igw-detail__row"><span class="igw-detail__k">' + k + '</span><span class="igw-detail__v">' + v + "</span></div>" : ""; }
    function num(n) { var x = Number(n); return isFinite(x) && x ? x.toLocaleString("en-IN") : ""; }
    function openPiece(a) {
      var imgs = (a.getAttribute("data-images") || "").split("|").filter(Boolean);
      var title = a.getAttribute("data-title") || "", color = a.getAttribute("data-color") || "";
      ownedEl.textContent = "Owned since " + (a.getAttribute("data-owned") || "");
      nameEl.textContent = (title.split("- ")[0] || title).trim();
      colorEl.textContent = color; colorEl.style.display = color ? "" : "none";
      var st = num(a.getAttribute("data-stitch"));
      craftEl.innerHTML =
        row("Craft Profile", a.getAttribute("data-profile") ? ("#" + a.getAttribute("data-profile")) : "") +
        row("Technique", a.getAttribute("data-technique")) +
        row("Composition", a.getAttribute("data-fabric")) +
        row("Embroidery hours", a.getAttribute("data-hours") ? ("≈" + a.getAttribute("data-hours") + " <small>hrs</small>") : "") +
        row("Estimated stitches", st ? (st + "+") : "");
      if (imgs.length) { imgEl.src = imgs[0]; imgEl.alt = title; }
      thumbs.innerHTML = imgs.length > 1 ? imgs.map(function (u, i) { return '<img class="igw-detail__thumb' + (i === 0 ? " is-on" : "") + '" src="' + u + '" alt="" data-i="' + i + '">'; }).join("") : "";
      linkEl.href = a.getAttribute("data-url") || "#";
      lastY = window.pageYOffset || 0; var b = document.body; b.style.position = "fixed"; b.style.top = (-lastY) + "px"; b.style.left = "0"; b.style.right = "0"; b.style.width = "100%";
      detail.hidden = false; requestAnimationFrame(function () { detail.classList.add("is-open"); });
    }
    function closePiece() {
      detail.classList.remove("is-open"); setTimeout(function () { detail.hidden = true; }, 460);
      var b = document.body; b.style.position = ""; b.style.top = ""; b.style.left = ""; b.style.right = ""; b.style.width = ""; window.scrollTo(0, lastY);
    }
    host.querySelectorAll(".igw-piece[data-ig-piece]").forEach(function (a) { a.addEventListener("click", function (e) { e.preventDefault(); openPiece(a); }); });
    detail.querySelectorAll("[data-ig-detail-close]").forEach(function (b) { b.addEventListener("click", closePiece); });
    thumbs.addEventListener("click", function (e) { var t = e.target.closest(".igw-detail__thumb"); if (!t) return; imgEl.src = t.src; thumbs.querySelectorAll(".igw-detail__thumb").forEach(function (x) { x.classList.remove("is-on"); }); t.classList.add("is-on"); });
    if (!window.__igwEsc) { window.__igwEsc = true; document.addEventListener("keydown", function (e) { if (e.key === "Escape") { var d = document.querySelector("[data-ig-detail].is-open"); if (d) { var x = d.querySelector("[data-ig-detail-close]"); if (x) x.click(); } } }); }
  }
  function renderWardrobe() {
    var host = document.getElementById("ig-wardrobe");
    if (!host) return;
    var seen = {}, pieces = [], minYear = 9999, hours = 0, stitches = 0;
    (state.orders || []).forEach(function (o) {
      var y = o.placedAt ? new Date(o.placedAt).getFullYear() : 0;
      if (y && y < minYear) minYear = y;
      (o.items || []).forEach(function (it) {
        var pr = it.product; if (!pr || !pr.id || seen[pr.id]) return;
        seen[pr.id] = 1;
        pieces.push({ p: pr, since: o.placedAt });
        if (pr.hours) hours += pr.hours;
        if (pr.stitches) stitches += pr.stitches;
      });
    });
    if (!pieces.length) {
      host.innerHTML =
        '<div class="igw-empty">' +
          '<p class="igw-empty__eyebrow">Your wardrobe</p>' +
          '<h2 class="igw-empty__title">Waiting for its first piece.</h2>' +
          '<p class="igw-empty__lead">When your first Indigrey garment arrives, it takes its place here — carrying the story of how it was made, the hands it passed through, and the Karkhana it came from.</p>' +
          '<a class="ig-btn ig-btn--primary" href="/collections/all">Explore the collection</a>' +
          '<p class="igw-meaning">Indigo — creativity, expression, individuality.<br>Grey — precision, discipline, craftsmanship.<br>Every piece lives where those two worlds meet.</p>' +
        '</div>';
      return;
    }
    var firstName = (state.me && state.me.profile && state.me.profile.firstName) || "";
    var yearFrom = minYear < 9999 ? minYear : new Date().getFullYear();
    var profiles = pieces.filter(function (x) { return x.p.profileNo; }).length || pieces.length;
    var artisans = pieces.length * 5;
    var FLATLAYS = {};
    try { var fx = document.querySelector("[data-ig-flatlays]"); if (fx) FLATLAYS = JSON.parse(fx.textContent || "{}") || {}; } catch (e) {}
    // A single owned garment, presented in its display niche.
    function pieceHTML(x, featured, idx) {
      var p = x.p;
      var handle = (p.url || "").split("/products/")[1];
      handle = handle ? handle.split("?")[0].split("#")[0].replace(/\/$/, "") : "";
      var flatLay = FLATLAYS[handle] || p.flat || "";
      var np = (p.title || "").split("- ");
      var color = np.length > 1 ? np[np.length - 1].trim() : "";
      var name = (np[0] || p.title || "").trim();
      var imgs = [flatLay, p.model].filter(Boolean).join("|");
      return '<a class="igw2-piece ig-tap' + (featured ? " igw2-piece--featured" : "") + '" data-ig-piece data-ig-rise style="transition-delay:' + (Math.min(idx, 8) * 70) + 'ms"' +
        ' href="' + esc(p.url || "#") + '" data-title="' + esc(p.title || "") + '" data-color="' + esc(color) + '" data-url="' + esc(p.url || "") + '"' +
        ' data-owned="' + esc(monthYear(x.since)) + '" data-profile="' + esc(p.profileNo || "") + '" data-hours="' + esc(String(p.hours || "")) + '" data-stitch="' + esc(String(p.stitches || "")) + '"' +
        ' data-technique="' + esc(p.technique || "") + '" data-fabric="' + esc(p.fabric || "") + '" data-images="' + esc(imgs) + '">' +
        '<div class="igw2-niche">' + (flatLay ? '<img class="igw2-img" src="' + esc(flatLay) + '" alt="' + esc(name) + '" loading="lazy">' : "") + "</div>" +
        '<div class="igw2-cap"><p class="igw2-name">' + esc(name) + "</p>" +
        (p.profileNo ? '<p class="igw2-meta">Craft Profile #' + esc(p.profileNo) + "</p>" : "") +
        '<p class="igw2-meta">Collected &middot; ' + esc(monthYear(x.since)) + "</p></div></a>";
    }
    // Curate the owned pieces into wardrobe chapters — never a product grid.
    function chapterOf(x) {
      var np = (x.p.title || "").split("- ");
      var c = (np.length > 1 ? np[np.length - 1] : "").toLowerCase();
      if (/white|ivory|cream|off.?white|pearl|snow|natural|beige/.test(c)) return "Summer Whites";
      if (/wine|black|maroon|navy|indigo|burgundy|charcoal|emerald|olive|rust|deep|red/.test(c)) return "Statement Pieces";
      return "Everyday Classics";
    }
    var CHAP = [
      { k: "Summer Whites", sub: "Light, luminous — made for warm days and slow afternoons." },
      { k: "Statement Pieces", sub: "The pieces that speak first. Worn when the room should turn." },
      { k: "Everyday Classics", sub: "Quiet companions, reached for again and again." }
    ];
    var ROMAN = ["I", "II", "III", "IV", "V"];
    var groups = {};
    pieces.forEach(function (x) { var c = chapterOf(x); (groups[c] = groups[c] || []).push(x); });
    var ci = 0;
    var chaptersHTML = CHAP.filter(function (c) { return groups[c.k] && groups[c.k].length; }).map(function (c) {
      var list = groups[c.k]; ci++;
      var body = list.map(function (x, i) { return pieceHTML(x, i === 0, i); }).join("");
      return '<section class="igw2-chapter">' +
        '<div class="igw2-chapter__head" data-ig-rise><p class="igw2-chapter__num">Chapter ' + ROMAN[ci - 1] + "</p>" +
        '<h3 class="igw2-chapter__title">' + esc(c.k) + "</h3>" +
        '<p class="igw2-chapter__sub">' + esc(c.sub) + "</p></div>" +
        '<div class="igw2-pieces">' + body + "</div></section>";
    }).join("");

    host.innerHTML =
      '<div class="igw2">' +
        '<header class="igw2-head" data-ig-rise>' +
          '<p class="igw2-eyebrow">Your Wardrobe</p>' +
          '<h2 class="igw2-title">' + (firstName ? esc(firstName) + "," : "Your collection,") + "</h2>" +
          '<p class="igw2-count">' + pieces.length + " Piece" + (pieces.length !== 1 ? "s" : "") + " Collected Since " + yearFrom + "</p>" +
          (hours > 0 ? '<p class="igw2-craftline">' + hours + " hours of hand embroidery <span class=\"igw2-dot\">&bull;</span> Over " + stitches.toLocaleString("en-IN") + " stitches across your wardrobe</p>" : "") +
        "</header>" +
        '<div class="igw2-summary" data-ig-rise>' +
          '<div class="igw2-stat"><span class="igw2-stat__num">' + pieces.length + '</span><span class="igw2-stat__lbl">Pieces Collected</span></div>' +
          '<div class="igw2-stat"><span class="igw2-stat__num">' + (hours || "—") + '</span><span class="igw2-stat__lbl">Hours of Hand Embroidery</span></div>' +
          '<div class="igw2-stat"><span class="igw2-stat__num">' + (stitches ? stitches.toLocaleString("en-IN") : "—") + '</span><span class="igw2-stat__lbl">Total Stitch Count</span></div>' +
          '<div class="igw2-stat"><span class="igw2-stat__num">' + profiles + '</span><span class="igw2-stat__lbl">Craft Profiles Owned</span></div>' +
          '<div class="igw2-stat"><span class="igw2-stat__num">' + artisans + '</span><span class="igw2-stat__lbl">Artisans Supported</span></div>' +
        "</div>" +
        chaptersHTML +
        '<section class="igw2-future" data-ig-rise>' +
          '<p class="igw2-chapter__num">Still being written</p>' +
          '<h3 class="igw2-chapter__title">Future Collection</h3>' +
          '<p class="igw2-future__sub">Every piece you add writes the next chapter. When your next Indigrey garment arrives, it takes its place here — carrying the hands and the hours behind it.</p>' +
        "</section>" +
      "</div>" +
      igwDetailHTML();
    igwDetailBind(host);
    armRise(host);
  }
  function statusChips(o) {
    if (o.cancelled) return chip("Cancelled", false, true);
    return chip(pretty(o.fulfillmentStatus), /fulfilled/i.test(o.fulfillmentStatus)) + " " + chip(pretty(o.paymentStatus));
  }
  // shipment journey — stage reached from the data we have (carrier micro-events
  // like "in transit"/"out for delivery" only light up if the service supplies them).
  var IGO_STAGES = ["Placed", "Preparing", "Shipped", "In transit", "Out for delivery", "Delivered"];
  function orderStage(o) {
    if (o.cancelled) return -1;
    if (/fulfilled|delivered/i.test(o.fulfillmentStatus || "")) return 5;
    if (o.tracking && o.tracking.length) return 3;
    return 1;
  }
  function orderHeadline(o) {
    if (o.cancelled) return "This order was cancelled";
    var s = orderStage(o);
    return s >= 5 ? "Delivered to your door." : s >= 3 ? "On its way to you." : "Being prepared with care.";
  }
  function timelineHTML(o) {
    if (o.cancelled) return '<p class="igo-timeline__note">This order was cancelled. Any payment is refunded to the original method.</p>';
    var idx = orderStage(o);
    var inset = 100 / IGO_STAGES.length / 2; // dots sit at flex-column centres, not the track edges
    var pct = idx <= 0 ? 0 : (idx / (IGO_STAGES.length - 1)) * (100 - inset * 2);
    var steps = IGO_STAGES.map(function (s, i) {
      var cls = i < idx ? "is-done" : (i === idx ? "is-current" : "");
      return '<div class="igo-step ' + cls + '"><span class="igo-step__dot"></span><span class="igo-step__label">' + s + "</span></div>";
    }).join("");
    return '<div class="igo-timeline"><div class="igo-timeline__track"><span class="igo-timeline__rail"></span><span class="igo-timeline__fill" style="width:' + pct + '%"></span>' + steps + "</div></div>";
  }
  function renderOrders() {
    var host = document.getElementById("ig-orders");
    if (!state.orders.length) { host.innerHTML = '<p class="ig-acct__empty">No orders yet. Your story with Indigrey begins with the first piece.</p>'; return; }
    host.innerHTML = '<p class="ig-acct__label">Your orders</p><div class="ig-acct__orders">' + state.orders.map(function (o, i) {
      var li = o.items[0] || {};
      return '<button type="button" class="ig-acct__ocard ig-acct__ocard--btn" data-ig-order="' + i + '" data-ig-rise style="transition-delay:' + (Math.min(i, 8) * 55) + 'ms">' +
        '<div class="ig-acct__ocard-main">' +
        (itemImage(li) ? '<img class="ig-acct__thumb ig-acct__thumb--sm" src="' + esc(itemImage(li)) + '" width="60" height="76" alt="" loading="lazy">' : "") +
        '<div class="ig-acct__ocard-info"><p class="ig-acct__order-name">' + esc(o.name) + "</p>" +
        '<p class="ig-acct__order-items">' + dt(o.placedAt) + " &middot; " + o.items.length + " item" + (o.items.length !== 1 ? "s" : "") + " &middot; " + money(o.total) + "</p>" +
        '<p class="ig-acct__chips">' + statusChips(o) + "</p></div></div>" +
        "</button>";
    }).join("") + "</div>" +
      (state.pageInfo && state.pageInfo.hasNextPage ? '<nav class="ig-acct__pagination"><button type="button" class="ig-btn ig-btn--sm" data-ig-more>Load more</button></nav>' : "") +
      igoDetailHTML();
    var more = host.querySelector("[data-ig-more]");
    if (more) more.addEventListener("click", function () { loadOrders(state.pageInfo.endCursor); });
    igoDetailBind(host);
    armRise(host);
  }
  function igoDetailHTML() {
    return '<div class="igo-detail" data-ig-odetail hidden>' +
      '<button class="igo-detail__scrim" data-ig-odetail-close aria-label="Close order"></button>' +
      '<div class="igo-detail__panel" role="dialog" aria-modal="true" aria-label="Order detail">' +
      '<button class="igw-detail__x" data-ig-odetail-close aria-label="Close"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg></button>' +
      '<div class="igo-detail__inner" data-ig-odetail-body></div></div></div>';
  }
  function igoDetailBind(host) {
    var d = host.querySelector("[data-ig-odetail]"); if (!d) return;
    var body = d.querySelector("[data-ig-odetail-body]"); var lastY = 0;
    function open(o) {
      var relig = returnEligibility(o);
      body.innerHTML =
        '<p class="igo-detail__eyebrow">' + esc(o.name) + " &middot; " + dt(o.placedAt) + "</p>" +
        '<h2 class="igo-detail__title">' + esc(orderHeadline(o)) + "</h2>" +
        timelineHTML(o) +
        (o.tracking && o.tracking.length ? '<div class="igo-detail__section igo-track"><p class="ig-acct__label">Track your parcel</p>' + o.tracking.map(function (t) {
          return '<p class="igo-track__meta">' + (t.courier ? esc(t.courier) + " &middot; " : "") + "Tracking ID <span class=\"igo-track__awb\">" + esc(t.awb || "—") + "</span>" + (t.shippedAt ? " &middot; shipped " + dt(t.shippedAt) : "") + "</p>" +
            (t.url ? '<a class="ig-btn ig-btn--primary igo-track__btn" href="' + esc(t.url) + '" target="_blank" rel="noopener">Track shipment &rarr;</a>' : "");
        }).join("") + "</div>" : "") +
        '<div class="igo-detail__section"><p class="ig-acct__label">Items</p>' + o.items.map(function (l) {
          return '<div class="igo-item">' + (itemImage(l) ? '<img class="igo-item__img" src="' + esc(itemImage(l)) + '" width="72" height="90" alt="" loading="lazy">' : "") +
            '<div class="igo-item__info"><p class="igo-item__title">' + esc(l.title) + "</p>" +
            '<p class="igo-item__meta">' + (l.variant && l.variant !== "Default Title" ? esc(l.variant) + " &middot; " : "") + "Qty " + l.quantity + (l.total ? " &middot; " + money(l.total) : "") + "</p></div></div>";
        }).join("") + "</div>" +
        '<div class="igo-detail__section"><p class="ig-acct__label">Summary</p><ul class="ig-acct__od-totals">' +
        (o.subtotal ? "<li><span>Subtotal</span><span>" + money(o.subtotal) + "</span></li>" : "") +
        (o.shipping && Number(o.shipping.amount) > 0 ? "<li><span>Shipping</span><span>" + money(o.shipping) + "</span></li>" : "") +
        (o.discounts && Number(o.discounts.amount) > 0 ? "<li><span>Discount</span><span>&minus;" + money(o.discounts) + "</span></li>" : "") +
        '<li class="ig-acct__od-total"><span>Total</span><span>' + money(o.total) + "</span></li></ul>" +
        (o.tax && Number(o.tax.amount) > 0 ? '<p class="ig-acct__od-item-meta" style="margin-top:6px">Includes GST of ' + money(o.tax) + "</p>" : "") + "</div>" +
        (o.deliverTo ? '<div class="igo-detail__section"><p class="ig-acct__label">Delivery address</p><p class="ig-acct__od-addr">' + [].concat(o.deliverTo).map(esc).join("<br>") + "</p></div>" : "") +
        (relig && relig.open ? '<div class="igo-detail__section"><p class="ig-acct__label">Return</p><p class="igr-window-note" style="margin:0 0 12px">Within your 7-day window — <b>' + relig.daysLeft + " day" + (relig.daysLeft !== 1 ? "s" : "") + ' left</b> to return a piece from this order.</p><button class="ig-btn ig-btn--primary" data-ig-order-return>Start a return</button></div>' : "") +
        '<p class="igo-detail__help">Need help with this order? <a class="ig-acct__od-link" href="/pages/contact">Contact us</a></p>';
      var orb = body.querySelector("[data-ig-order-return]"); if (orb) orb.addEventListener("click", function () { close(); setTimeout(function () { openStartReturn({ order: o, elig: relig }); }, 240); });
      lastY = window.pageYOffset || 0; var b = document.body; b.style.position = "fixed"; b.style.top = (-lastY) + "px"; b.style.left = "0"; b.style.right = "0"; b.style.width = "100%";
      d.hidden = false; requestAnimationFrame(function () { d.classList.add("is-open"); if (body.parentElement) body.parentElement.scrollTop = 0; });
    }
    function close() { d.classList.remove("is-open"); setTimeout(function () { d.hidden = true; }, 460); var b = document.body; b.style.position = ""; b.style.top = ""; b.style.left = ""; b.style.right = ""; b.style.width = ""; window.scrollTo(0, lastY); }
    host.querySelectorAll("[data-ig-order]").forEach(function (btn) { btn.addEventListener("click", function () { var o = state.orders[+btn.getAttribute("data-ig-order")]; if (o) open(o); }); });
    d.querySelectorAll("[data-ig-odetail-close]").forEach(function (b) { b.addEventListener("click", close); });
    if (!window.__igoEsc) { window.__igoEsc = true; document.addEventListener("keydown", function (e) { if (e.key === "Escape") { var x = document.querySelector("[data-ig-odetail].is-open [data-ig-odetail-close]"); if (x) x.click(); } }); }
  }

  // ---------- returns ----------
  // Same visual language as Orders: a list of returns → a detail panel with a
  // horizontal journey (Requested → Approved → Picked up → In transit →
  // Received → Refunded). Live data needs a /v1/returns endpoint on the account
  // service; until then — or when there are none — the designed empty state shows.
  var IGR_STAGES = ["Requested", "Approved", "Picked up", "In transit", "Received", "Refunded"];
  var IGX_STAGES = ["Requested", "Approved", "Picked up", "Received", "New piece shipped", "Delivered"];
  function isExchange(r) { return String(r && r.type || "").toLowerCase() === "exchange"; }
  function rStages(r) { return isExchange(r) ? IGX_STAGES : IGR_STAGES; }
  function returnStage(r) {
    var s = (r.status || "").toLowerCase();
    if (r.declined || /declin|reject/.test(s)) return -1;
    if (isExchange(r)) {
      if (/deliver|complete|closed/.test(s)) return 5;
      if (/exchange.?ship|reship|new.?piece|dispatch/.test(s)) return 4;
      if (/receiv|inspect/.test(s)) return 3;
      if (/pick/.test(s)) return 2;
      if (/approv|accept/.test(s)) return 1;
      return 0;
    }
    if (/refund|closed|complete/.test(s)) return 5;
    if (/receiv|inspect/.test(s)) return 4;
    if (/transit|shipp/.test(s)) return 3;
    if (/pick/.test(s)) return 2;
    if (/approv|accept/.test(s)) return 1;
    return 0;
  }
  function returnHeadline(r) {
    var s = returnStage(r), x = isExchange(r);
    if (s < 0) return x ? "This exchange was declined" : "This return was declined";
    if (x) return s >= 5 ? "Your new piece has arrived." : s >= 4 ? "Your exchange is on its way." : s >= 2 ? "Your piece is on its way back." : "Exchange requested — we’re on it.";
    return s >= 5 ? "Refunded to your original method." : s >= 2 ? "Your piece is on its way back." : "Return requested — we’re on it.";
  }
  function returnTimelineHTML(r) {
    if (returnStage(r) < 0) return '<p class="igo-timeline__note">This ' + (isExchange(r) ? "exchange" : "return") + ' request wasn’t approved. Our team will have shared the reason — reply to that message or contact us and we’ll help.</p>';
    var stages = rStages(r), idx = returnStage(r);
    var inset = 100 / stages.length / 2;
    var pct = idx <= 0 ? 0 : (idx / (stages.length - 1)) * (100 - inset * 2);
    var steps = stages.map(function (s, i) {
      var cls = i < idx ? "is-done" : (i === idx ? "is-current" : "");
      return '<div class="igo-step ' + cls + '"><span class="igo-step__dot"></span><span class="igo-step__label">' + s + "</span></div>";
    }).join("");
    return '<div class="igo-timeline"><div class="igo-timeline__track"><span class="igo-timeline__rail"></span><span class="igo-timeline__fill" style="width:' + pct + '%"></span>' + steps + "</div></div>";
  }
  function returnChip(r) {
    if (returnStage(r) < 0) return chip("Declined", false, true);
    return chip(isExchange(r) ? "Exchange" : "Return") + " " + chip(pretty(r.status) || "Requested", returnStage(r) >= 5);
  }
  // Returns are open for 7 DAYS AFTER DELIVERY. Eligibility + the countdown are
  // computed from each order's delivery date.
  function returnEligibility(o) {
    if (!o || o.cancelled) return null;
    var dd = o.deliveredAt || null;
    if (!dd) return null;
    var left = Math.ceil((new Date(dd).getTime() + 7 * 864e5 - Date.now()) / 864e5);
    return { deliveredAt: dd, daysLeft: left, open: left > 0 };
  }
  function isReturnsDemo() { return /[?&]returns_demo=1/i.test(location.search); }
  function loadReturns() {
    var host = document.getElementById("ig-returns"); if (!host) return;
    if (state._returnsLoaded) { renderReturns(); return; }
    api("/v1/returns").then(function (res) { state.returns = (res && res.returns) || []; finishReturns(); })
      .catch(function () { state.returns = []; finishReturns(); }); // endpoint not live yet → calm fallback, not an error
  }
  function finishReturns() {
    if (isReturnsDemo()) seedDemoOrder();   // make the customer's first order a delivered, in-window one
    // returnable pieces = delivered orders still inside the 7-day window (real products)
    state.returnable = (state.orders || []).map(function (o) {
      var e = returnEligibility(o); return e && e.open ? { order: o, elig: e } : null;
    }).filter(Boolean);
    if (isReturnsDemo()) seedDemoReturn();  // one active, in-transit return to show the tracking flow
    state._returnsLoaded = true; renderReturns();
    if (isReturnsDemo()) renderOrders();    // reflect the demo order's new "delivered" state in Orders too
  }
  // Preview seed (?returns_demo=1): uses a REAL product from the customer's own
  // orders so the whole flow — including the return option inside the order detail —
  // can be seen end-to-end. Gated to the flag; real customers never see it.
  function seedDemoOrder() {
    var o = (state.orders || [])[0]; if (!o) return;
    o.deliveredAt = new Date(Date.now() - 3 * 864e5).toISOString();
    o.cancelled = false; o.fulfillmentStatus = "fulfilled";
    o.tracking = [{ shippedAt: new Date(Date.now() - 6 * 864e5).toISOString(), courier: "Delhivery", awb: "DL5842291170", url: "https://www.delhivery.com/track" }];
  }
  function seedDemoReturn() {
    var src = (state.orders || [])[0] || {}, li = (src.items && src.items[0]) || {};
    state.returns.unshift(
      { demo: true, type: "return", name: "RET-1024", orderName: src.name || "IND-1047", requestedAt: new Date(Date.now() - 2 * 864e5).toISOString(), status: "in_transit", items: [{ title: li.title || "Ambi Embroidered Top", image: li.image || "", quantity: 1, variant: "Wine", reason: "Didn’t fit" }], refund: { amount: 1199, currency: "INR" }, courier: "Delhivery", awb: "DL84421097", trackUrl: "#" },
      { demo: true, type: "exchange", name: "EXC-1031", orderName: src.name || "IND-1047", requestedAt: new Date(Date.now() - 1 * 864e5).toISOString(), status: "picked_up", items: [{ title: li.title || "Ambi Embroidered Top", image: li.image || "", quantity: 1, variant: "Wine", reason: "Didn’t fit" }], exchangeFor: "Same piece — size L", courier: "Delhivery", awb: "DL77120934", trackUrl: "#" }
    );
  }
  function eligCard(x, i) {
    var o = x.order, li = (o.items && o.items[0]) || {}, soon = x.elig.daysLeft <= 2;
    return '<button type="button" class="ig-acct__ocard ig-acct__ocard--btn" data-ig-return-start="' + i + '" data-ig-rise style="transition-delay:' + (Math.min(i, 8) * 55) + 'ms">' +
      '<div class="ig-acct__ocard-main">' +
      (itemImage(li) ? '<img class="ig-acct__thumb ig-acct__thumb--sm" src="' + esc(itemImage(li)) + '" width="60" height="76" alt="" loading="lazy">' : "") +
      '<div class="ig-acct__ocard-info"><p class="ig-acct__order-name">' + esc(li.title || "Your piece") + "</p>" +
      '<p class="ig-acct__order-items">From ' + esc(o.name || "") + " &middot; delivered " + dt(o.deliveredAt) + "</p>" +
      '<p class="ig-acct__chips"><span class="igr-days' + (soon ? " igr-days--soon" : "") + '">' + x.elig.daysLeft + " day" + (x.elig.daysLeft !== 1 ? "s" : "") + " left to return</span></p></div></div>" +
      "</button>";
  }
  function returnCard(r, i) {
    var li = (r.items && r.items[0]) || {};
    return '<button type="button" class="ig-acct__ocard ig-acct__ocard--btn" data-ig-return="' + i + '" data-ig-rise style="transition-delay:' + (Math.min(i, 8) * 55) + 'ms">' +
      '<div class="ig-acct__ocard-main">' +
      (itemImage(li) ? '<img class="ig-acct__thumb ig-acct__thumb--sm" src="' + esc(itemImage(li)) + '" width="60" height="76" alt="" loading="lazy">' : "") +
      '<div class="ig-acct__ocard-info"><p class="ig-acct__order-name">' + esc(r.name || ("Return " + (i + 1))) + "</p>" +
      '<p class="ig-acct__order-items">' + (r.requestedAt ? "Requested " + dt(r.requestedAt) : "") + (r.orderName ? " &middot; for " + esc(r.orderName) : "") + "</p>" +
      '<p class="ig-acct__chips">' + returnChip(r) + "</p></div></div>" +
      '<span class="ig-acct__ocard-go" aria-hidden="true"><svg width="8" height="13" viewBox="0 0 8 13" fill="none"><path d="M1 1l6 5.5-6 5.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg></span>' +
      "</button>";
  }
  function renderReturns() {
    var host = document.getElementById("ig-returns"); if (!host) return;
    var elig = state.returnable || [], active = state.returns || [];
    if (!elig.length && !active.length) {
      host.innerHTML = '<div class="igw-empty"><p class="igw-empty__eyebrow">Your returns</p>' +
        '<h2 class="igw-empty__title">Nothing to send back.</h2>' +
        '<p class="igw-empty__lead">We hope every piece feels right the moment it arrives. If one ever isn’t, you have <b>7 days from delivery</b> to return it — begin from any delivered order, and follow it here step by step.</p></div>';
      return;
    }
    var html = "";
    if (elig.length) {
      html += '<p class="ig-acct__label">Return a piece</p>' +
        '<p class="igr-window-note">Returns are accepted within <b>7 days of delivery</b>, in original condition with tags. Choose a piece to begin.</p>' +
        '<div class="ig-acct__orders">' + elig.map(eligCard).join("") + "</div>";
    }
    if (active.length) {
      html += '<p class="ig-acct__label"' + (elig.length ? ' style="margin-top:34px"' : "") + ">Your returns &amp; exchanges</p>" +
        '<div class="ig-acct__orders">' + active.map(returnCard).join("") + "</div>";
    }
    host.innerHTML = html + igrDetailHTML();
    igrDetailBind(host);
    host.querySelectorAll("[data-ig-return-start]").forEach(function (btn) { btn.addEventListener("click", function () { var x = state.returnable[+btn.getAttribute("data-ig-return-start")]; if (x) openStartReturn(x); }); });
    armRise(host);
  }
  function igrDetailHTML() {
    return '<div class="igo-detail" data-ig-rdetail hidden>' +
      '<button class="igo-detail__scrim" data-ig-rdetail-close aria-label="Close return"></button>' +
      '<div class="igo-detail__panel" role="dialog" aria-modal="true" aria-label="Return detail">' +
      '<button class="igw-detail__x" data-ig-rdetail-close aria-label="Close"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg></button>' +
      '<div class="igo-detail__inner" data-ig-rdetail-body></div></div></div>';
  }
  function igrDetailBind(host) {
    var d = host.querySelector("[data-ig-rdetail]"); if (!d) return;
    var body = d.querySelector("[data-ig-rdetail-body]"); var lastY = 0;
    function open(r) {
      body.innerHTML =
        '<p class="igo-detail__eyebrow">' + esc(r.name || "Return") + (r.requestedAt ? " &middot; " + dt(r.requestedAt) : "") + "</p>" +
        '<h2 class="igo-detail__title">' + esc(returnHeadline(r)) + "</h2>" +
        returnTimelineHTML(r) +
        (r.trackUrl ? '<p class="igo-detail__track">In transit' + (r.courier ? " via " + esc(r.courier) : "") + ' &middot; <a class="ig-acct__od-link" href="' + esc(r.trackUrl) + '" target="_blank" rel="noopener">Track' + (r.awb ? " " + esc(r.awb) : "") + "</a></p>" : "") +
        (r.orderName ? '<p class="igo-detail__track">From order ' + esc(r.orderName) + "</p>" : "") +
        '<div class="igo-detail__section"><p class="ig-acct__label">Items</p>' + ((r.items || []).map(function (l) {
          return '<div class="igo-item">' + (itemImage(l) ? '<img class="igo-item__img" src="' + esc(itemImage(l)) + '" width="72" height="90" alt="" loading="lazy">' : "") +
            '<div class="igo-item__info"><p class="igo-item__title">' + esc(l.title) + "</p>" +
            '<p class="igo-item__meta">' + (l.variant && l.variant !== "Default Title" ? esc(l.variant) + " &middot; " : "") + "Qty " + (l.quantity || 1) + (l.reason ? " &middot; " + esc(l.reason) : "") + "</p></div></div>";
        }).join("")) + "</div>" +
        (isExchange(r) ? '<div class="igo-detail__section"><p class="ig-acct__label">Exchange for</p><p class="ig-acct__od-addr">' + esc(r.exchangeFor || "A different size or colour") + '</p><p class="ig-acct__od-item-meta" style="margin-top:6px">' + (returnStage(r) >= 5 ? "Your new piece has been delivered." : "We ship your new piece once the returned one is received and checked.") + "</p></div>" :
          (((r.refund && Number(r.refund.amount) > 0) || r.refundAmount) ? '<div class="igo-detail__section"><p class="ig-acct__label">Refund</p><ul class="ig-acct__od-totals"><li class="ig-acct__od-total"><span>' + (returnStage(r) >= 5 ? "Refunded" : "Refund due") + '</span><span>' + money(r.refund || { amount: r.refundAmount }) + "</span></li></ul>" + '<p class="ig-acct__od-item-meta" style="margin-top:6px">To your original payment method' + (returnStage(r) >= 5 ? "." : " once the piece is received and checked.") + "</p></div>" : "")) +
        '<p class="igo-detail__help">Questions about this ' + (isExchange(r) ? "exchange" : "return") + '? <a class="ig-acct__od-link" href="/pages/contact">Contact us</a></p>';
      lastY = window.pageYOffset || 0; var b = document.body; b.style.position = "fixed"; b.style.top = (-lastY) + "px"; b.style.left = "0"; b.style.right = "0"; b.style.width = "100%";
      d.hidden = false; requestAnimationFrame(function () { d.classList.add("is-open"); if (body.parentElement) body.parentElement.scrollTop = 0; });
    }
    function close() { d.classList.remove("is-open"); setTimeout(function () { d.hidden = true; }, 460); var b = document.body; b.style.position = ""; b.style.top = ""; b.style.left = ""; b.style.right = ""; b.style.width = ""; window.scrollTo(0, lastY); }
    host.querySelectorAll("[data-ig-return]").forEach(function (btn) { btn.addEventListener("click", function () { var r = state.returns[+btn.getAttribute("data-ig-return")]; if (r) open(r); }); });
    d.querySelectorAll("[data-ig-rdetail-close]").forEach(function (b) { b.addEventListener("click", close); });
    if (!window.__igrEsc) { window.__igrEsc = true; document.addEventListener("keydown", function (e) { if (e.key === "Escape") { var x = document.querySelector("[data-ig-rdetail].is-open [data-ig-rdetail-close]"); if (x) x.click(); } }); }
  }
  // Start-a-return flow — reason picker → request (posts to /v1/returns when the
  // endpoint exists; otherwise a graceful confirmation).
  var IGR_REASONS = ["Didn’t fit", "Not as expected", "Changed my mind", "Fabric / quality", "Received wrong item", "Arrived damaged"];
  function igrStartHTML() {
    return '<div class="igo-detail" data-ig-rstart hidden>' +
      '<button class="igo-detail__scrim" data-ig-rstart-close aria-label="Close"></button>' +
      '<div class="igo-detail__panel" role="dialog" aria-modal="true" aria-label="Start a return">' +
      '<button class="igw-detail__x" data-ig-rstart-close aria-label="Close"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.4"/></svg></button>' +
      '<div class="igo-detail__inner" data-ig-rstart-body></div></div></div>';
  }
  // Singleton start-return overlay so it can be opened from BOTH the Returns tab
  // (an eligible piece) and the Orders tab (an order still in its 7-day window).
  var _igrStartEl = null, _igrStartY = 0;
  function ensureStartOverlay() {
    if (_igrStartEl && document.body.contains(_igrStartEl)) return _igrStartEl;
    var w = document.createElement("div"); w.innerHTML = igrStartHTML(); _igrStartEl = w.firstElementChild;
    (app || document.body).appendChild(_igrStartEl);
    _igrStartEl.querySelectorAll("[data-ig-rstart-close]").forEach(function (b) { b.addEventListener("click", closeStartReturn); });
    if (!window.__igrsEsc) { window.__igrsEsc = true; document.addEventListener("keydown", function (e) { if (e.key === "Escape") { var x = document.querySelector("[data-ig-rstart].is-open [data-ig-rstart-close]"); if (x) x.click(); } }); }
    return _igrStartEl;
  }
  function closeStartReturn() {
    var d = _igrStartEl; if (!d) return; d.classList.remove("is-open"); setTimeout(function () { d.hidden = true; }, 460);
    var b = document.body; b.style.position = ""; b.style.top = ""; b.style.left = ""; b.style.right = ""; b.style.width = ""; window.scrollTo(0, _igrStartY);
  }
  var IGX_SIZES = ["Same piece — size XS", "Same piece — size S", "Same piece — size M", "Same piece — size L", "Same piece — size XL", "A different colour (we’ll email the options)"];
  function openStartReturn(x) {
    var d = ensureStartOverlay(), body = d.querySelector("[data-ig-rstart-body]");
    var o = x.order, li = (o.items && o.items[0]) || {};
    body.innerHTML =
      '<p class="igo-detail__eyebrow">Return or exchange &middot; ' + x.elig.daysLeft + " day" + (x.elig.daysLeft !== 1 ? "s" : "") + " left</p>" +
      '<h2 class="igo-detail__title">This piece.</h2>' +
      '<div class="igo-item" style="margin:2px 0 20px">' + (itemImage(li) ? '<img class="igo-item__img" src="' + esc(itemImage(li)) + '" width="72" height="90" alt="">' : "") +
      '<div class="igo-item__info"><p class="igo-item__title">' + esc(li.title || "Your piece") + '</p><p class="igo-item__meta">From ' + esc(o.name || "") + " &middot; delivered " + dt(o.deliveredAt) + "</p></div></div>" +
      '<div class="igx-choice">' +
        '<button type="button" class="igx-opt is-on" data-igx-type="return"><span class="igx-opt__t">Return</span><span class="igx-opt__s">Refund to original method</span></button>' +
        '<button type="button" class="igx-opt" data-igx-type="exchange"><span class="igx-opt__t">Exchange</span><span class="igx-opt__s">Different size or colour</span></button>' +
      '</div>' +
      '<p class="ig-acct__label" style="margin-top:22px">Reason</p>' +
      '<div class="igr-choices" data-ig-reasons>' + IGR_REASONS.map(function (r, i) { return '<button type="button" class="igr-choice' + (i === 0 ? " is-on" : "") + '" data-val="' + esc(r) + '">' + esc(r) + "</button>"; }).join("") + "</div>" +
      '<div data-igx-for hidden><p class="ig-acct__label" style="margin-top:18px">Exchange for</p>' +
        '<div class="igr-choices" data-igx-sizes>' + IGX_SIZES.map(function (v, i) { return '<button type="button" class="igr-choice' + (i === 0 ? " is-on" : "") + '" data-val="' + esc(v) + '">' + esc(v) + "</button>"; }).join("") + "</div></div>" +
      '<p class="igr-window-note" data-igx-note style="margin-top:16px"></p>' +
      '<div style="margin-top:20px"><button class="ig-btn ig-btn--primary" data-ig-rstart-submit>Request return</button></div>' +
      '<p class="ig-acct__od-item-meta" data-ig-rstart-msg style="margin-top:12px;min-height:16px"></p>';
    var opts = body.querySelectorAll("[data-igx-type]"), forBlock = body.querySelector("[data-igx-for]"), note = body.querySelector("[data-igx-note]");
    var sub = body.querySelector("[data-ig-rstart-submit]"), msg = body.querySelector("[data-ig-rstart-msg]"), type = "return";
    var RNOTE = "Once you request, we confirm by email and arrange a pickup — track it here step by step. The refund goes to your original payment method after the piece is received and checked.";
    var XNOTE = "Once you request, we confirm by email and arrange a pickup. After we receive and check the piece, we ship your new one — track the whole exchange here.";
    function setType(t) {
      type = t;
      opts.forEach(function (b) { b.classList.toggle("is-on", b.getAttribute("data-igx-type") === t); });
      forBlock.hidden = t !== "exchange";
      note.textContent = t === "exchange" ? XNOTE : RNOTE;
      sub.textContent = t === "exchange" ? "Request exchange" : "Request return";
    }
    opts.forEach(function (b) { b.addEventListener("click", function () { setType(b.getAttribute("data-igx-type")); }); });
    body.querySelectorAll(".igr-choices").forEach(function (list) {
      list.addEventListener("click", function (e) { var b = e.target.closest(".igr-choice"); if (!b) return; list.querySelectorAll(".igr-choice").forEach(function (c) { c.classList.remove("is-on"); }); b.classList.add("is-on"); });
    });
    function pick(sel) { var e = body.querySelector(sel + " .is-on"); return e ? e.getAttribute("data-val") : ""; }
    setType("return");
    sub.addEventListener("click", function () {
      var reason = pick("[data-ig-reasons]");
      var exchangeFor = type === "exchange" ? pick("[data-igx-sizes]") : "";
      sub.disabled = true; sub.textContent = type === "exchange" ? "Requesting exchange…" : "Requesting…";
      api("/v1/returns", { method: "POST", body: { type: type, orderName: o.name, reason: reason, exchangeFor: exchangeFor, items: (o.items || []).map(function (l) { return { title: l.title, quantity: l.quantity }; }) } })
        .then(function () { msg.textContent = "Requested — we’ll confirm by email; it’ll appear below."; state._returnsLoaded = false; setTimeout(function () { closeStartReturn(); var rt = app.querySelector('[data-ig-tab="returns"]'); if (rt) rt.click(); }, 1300); })
        .catch(function () { sub.textContent = (type === "exchange" ? "Exchange" : "Return") + " requested"; msg.textContent = "Thank you — your request is noted. We’ll confirm by email and arrange a pickup; you’ll then track it here."; });
    });
    _igrStartY = window.pageYOffset || 0; var b = document.body; b.style.position = "fixed"; b.style.top = (-_igrStartY) + "px"; b.style.left = "0"; b.style.right = "0"; b.style.width = "100%";
    d.hidden = false; requestAnimationFrame(function () { d.classList.add("is-open"); });
  }

  // ---------- addresses ----------
  function loadAddresses() {
    api("/v1/addresses").then(function (res) {
      state.addresses = res.addresses; state.defaultId = res.defaultId;
      var c = document.getElementById("ig-addr-count"); if (c) c.textContent = res.addresses.length;
      renderAddresses();
    }).catch(function () { document.getElementById("ig-addresses").innerHTML = '<p class="ig-acct__empty">Couldn’t load addresses.</p>'; });
  }
  function renderAddresses(editing) {
    var host = document.getElementById("ig-addresses");
    host.innerHTML = '<p class="ig-acct__label">' + state.addresses.length + " saved address" + (state.addresses.length !== 1 ? "es" : "") + "</p>" +
      '<div class="ig-acct__addr-grid">' + state.addresses.map(function (a, i) {
        return '<div class="ig-acct__card ig-acct__addr">' +
          (a.id === state.defaultId ? '<span class="ig-acct__addr-default">Default</span>' : "") +
          '<p class="ig-acct__addr-name">' + esc(((a.firstName || "") + " " + (a.lastName || "")).trim() || "Address") + "</p>" +
          '<p class="ig-acct__od-addr">' +
          esc(a.address1 || "") + (a.address2 ? "<br>" + esc(a.address2) : "") + "<br>" +
          esc(((a.zip || "") + " " + (a.city || "") + " " + (a.province || "")).trim()) + "<br>India" +
          (a.phone ? "<br>" + esc(a.phone) : "") + "</p>" +
          '<div class="ig-acct__addr-actions"><button class="ig-acct__linkbtn" data-ig-addr-edit="' + i + '">Edit</button>' +
          '<button class="ig-acct__linkbtn ig-acct__linkbtn--del" data-ig-addr-del="' + i + '">Remove</button></div></div>';
      }).join("") + "</div>" +
      '<div style="margin-top:24px"><button class="ig-btn ig-btn--primary" data-ig-addr-new>Add new address</button></div>' +
      '<div id="ig-addr-form"></div>';
    if (editing !== undefined) addrForm(editing);
    host.onclick = function (e) {
      var n = e.target.closest("[data-ig-addr-new]"); if (n) return addrForm(null);
      var ed = e.target.closest("[data-ig-addr-edit]"); if (ed) return addrForm(Number(ed.getAttribute("data-ig-addr-edit")));
      var dl = e.target.closest("[data-ig-addr-del]");
      if (dl && window.confirm("Remove this address?")) {
        var a = state.addresses[Number(dl.getAttribute("data-ig-addr-del"))];
        api("/v1/addresses/" + encodeURIComponent(a.id), { method: "DELETE" }).then(loadAddresses)
          .catch(function (err) { alert(err.error || "Could not delete"); });
      }
    };
  }
  function addrForm(index) {
    var a = index === null ? {} : state.addresses[index];
    var f = document.getElementById("ig-addr-form");
    var fields = [["firstName", "First name"], ["lastName", "Last name"], ["phone", "Mobile"], ["address1", "Address line 1"], ["address2", "Address line 2 (optional)"], ["city", "City"], ["province", "State"], ["zip", "PIN code"]];
    f.innerHTML = '<div class="ig-acct__card" style="margin-top:16px"><p class="ig-acct__label">' + (index === null ? "New address" : "Edit address") + "</p>" +
      '<div class="ig-addr-fields">' + fields.map(function (fd) {
        return '<label class="ig-addr-field"><span>' + fd[1] + '</span><input name="' + fd[0] + '" value="' + esc(a[fd[0]] || "") + '"></label>';
      }).join("") + "</div>" +
      '<label class="ig-addr-field ig-addr-field--check"><input type="checkbox" name="setDefault"' + (a.id && a.id === state.defaultId ? " checked" : "") + "> Set as default</label>" +
      '<div style="margin-top:14px"><button class="ig-btn ig-btn--primary" data-ig-addr-save>Save address</button>' +
      '<button class="ig-btn" data-ig-addr-cancel>Cancel</button></div><p class="ig-acct__od-item-meta" id="ig-addr-err"></p></div>';
    f.querySelector("[data-ig-addr-cancel]").onclick = function () { f.innerHTML = ""; };
    f.querySelector("[data-ig-addr-save]").onclick = function () {
      var body = {};
      f.querySelectorAll("input[name]").forEach(function (inp) { body[inp.name] = inp.type === "checkbox" ? inp.checked : inp.value; });
      var req = index === null ? api("/v1/addresses", { method: "POST", body: body })
        : api("/v1/addresses/" + encodeURIComponent(a.id), { method: "PUT", body: body });
      req.then(loadAddresses).catch(function (err) { document.getElementById("ig-addr-err").textContent = err.error || "Could not save"; });
    };
  }

  // ---------- profile + completion card ----------
  function renderProfile() {
    var host = document.getElementById("ig-profile");
    var p = state.me.profile, r = state.me.reward || {};
    host.innerHTML = '<div class="ig-acct__grid"><div class="ig-acct__card">' +
      '<p class="ig-acct__label">Your details</p><div class="ig-addr-fields">' +
      '<label class="ig-addr-field"><span>First name</span><input name="firstName" value="' + esc(p.firstName) + '"></label>' +
      '<label class="ig-addr-field"><span>Last name</span><input name="lastName" value="' + esc(p.lastName) + '"></label>' +
      '<label class="ig-addr-field"><span>Mobile (your Indigrey identity)</span><input value="' + esc(p.phone || "") + '" disabled></label>' +
      '<label class="ig-addr-field"><span>Email (for updates only — never needed to sign in)</span><input name="email" type="email" value="' + esc(p.email || "") + '"></label></div>' +
      '<div style="margin-top:14px"><button class="ig-btn ig-btn--primary" data-ig-profile-save>Save</button></div>' +
      '<p class="ig-acct__od-item-meta" id="ig-profile-msg"></p></div>' +
      (!state.me.profileComplete && r.amount > 0 ?
        '<div class="ig-acct__card ig-acct__pc-card"><p class="ig-acct__label">Complete your profile</p>' +
        '<p class="ig-acct__pc-lead">Add your email to receive:</p>' +
        '<ul class="ig-acct__pc-benefits"><li>Order confirmations</li><li>Shipping updates</li><li>GST invoices</li><li>Early access to new collections</li><li>Priority customer support</li></ul>' +
        '<div class="ig-acct__pc-reward"><span class="ig-acct__pc-reward-amt">₹' + r.amount + " Indigrey Credit</span>" +
        '<span class="ig-acct__pc-reward-terms">On orders above ₹' + r.minOrder + " &middot; Valid " + r.validityDays + " days</span></div></div>" : "") +
      "</div>";
    host.querySelector("[data-ig-profile-save]").onclick = function () {
      var body = {};
      host.querySelectorAll("input[name]").forEach(function (inp) { body[inp.name] = inp.value; });
      api("/v1/me", { method: "PATCH", body: body }).then(function () {
        document.getElementById("ig-profile-msg").textContent = "Saved.";
        return api("/v1/me");
      }).then(function (me) { state.me = me; renderProfile(); })
        .catch(function (err) { document.getElementById("ig-profile-msg").textContent = err.error || "Could not save"; });
    };
  }

  // Boot straight into the dashboard (one request). The provider is only needed
  // when logged out, so we fetch /v1/config lazily on the 401 path — logged-in
  // customers never pay for it.
  boot();
})();
